/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import React, { useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { useNavigate } from "react-router-dom";

interface BarChartProps {
  data: Record<string, number>[];
  label: string;
  series: string;
  dataKey: string;
  queryString?: string;
  disabledNavigation?: boolean;
}

export default function BarChart({
  data,
  label,
  series,
  dataKey,
  queryString,
  disabledNavigation,
}: Readonly<BarChartProps>) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  // Memoize chart series and labels based on data
  const chartSeries = useMemo(() => {
    return data.length
      ? [{ name: "Tickets", data: data.map((item) => Number(item[series])) }]
      : [{ name: "Tickets", data: [0] }];
  }, [data, series]);

  // Ensure labels are always strings
  const chartLabels = useMemo(() => {
    return data.length ? data.map((item) => String(item[label])) : [""];
  }, [data, label]);

  // Memoize chart options to avoid recreating them on every render
  const chartOptions = useMemo<ApexCharts.ApexOptions>(
    () => ({
      colors: [
        "#D4BD95",
        "#615034",
        "#877049",
        "#C1A068",
        "#D4BD95",
        "#E6D9C3",
        "#F9F6F0",
      ],
      chart: {
        type: "bar",
        toolbar: {
          show: false,
        },
        events: {
          dataPointSelection(event, chartContext, options) {
            const clickedPoint = options?.dataPointIndex;

            // Validate clickedPoint and data
            if (
              clickedPoint === undefined ||
              !data.length ||
              !chartLabels[clickedPoint]
            ) {
              // eslint-disable-next-line no-console
              console.warn(
                "Clicked data is out of bounds or undefined",
                clickedPoint
              );
              return;
            }

            const activityType = data[clickedPoint]?.[dataKey];
            if (!disabledNavigation) {
              if (activityType) {
                setTimeout(() => {
                  // eslint-disable-next-line no-console
                  console.log("Clicked Value:", dataKey, activityType);
                  navigate(
                    `/dashboard-detail?${dataKey}=${activityType}${queryString}`
                  );
                }, 100);
              }
            }
          },
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "55%",
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        show: true,
        width: 2,
        colors: ["transparent"],
      },
      xaxis: {
        labels: {
          show: false,
        },
      },
      legend: {
        show: false,
      },
      fill: {
        opacity: 1,
      },
      tooltip: {
        y: {
          formatter(val) {
            return `${val} orders`;
          },
        },
      },
    }),

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, dataKey, chartLabels, queryString]
  );

  // Update state only when necessary
  const [state, setState] = useState({
    options: chartOptions,
    series: chartSeries,
  });

  useEffect(() => {
    if (data.length) {
      setIsLoading(false);
      setState({
        options: { ...chartOptions, labels: chartLabels },
        series: chartSeries,
      });
    }
  }, [data, chartLabels, chartSeries, chartOptions]);

  if (isLoading) {
    return <div />;
  }

  return (
    <ReactApexChart
      options={state.options}
      series={state.series}
      type="bar"
      height={350}
    />
  );
}
